// ATLAS — Book-of-business steward dashboard.
//
// Where RETAIN watches who's about to LAPSE (life-only), ATLAS
// proactively GROWS the book across BOTH verticals:
//   - cross-sell queue, sorted by opportunity score
//   - annual review window (30 / 60 / 90 day countdown)
//   - NPS / happy-call moments (60-day, 1-yr, 2-yr, 5-yr anniversaries)
//   - open ATLAS tasks list
//
// Read-mostly. One-click Call fires the existing crm-dial-request
// CustomEvent so the CallWidget at app-root picks it up — same
// pipeline DIAL, the Inbox, the live-call task console, and the
// Territory drill-down already use.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import {
  Briefcase, Phone, MessageSquare, RefreshCcw, Loader2,
  Calendar, Heart, Plus, ChevronRight, Sparkles,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function fmtCents(cents) {
  const n = Number(cents) || 0;
  if (n === 0) return '—';
  return `$${(n / 100).toFixed(0)}/mo`;
}

function fmtDays(days) {
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 30)  return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}yr`;
}

function lobLabel(lob) {
  if (!lob) return '';
  const map = {
    aca: 'ACA', medicare: 'Medicare', medigap: 'Medigap',
    dental_vision: 'Dental/Vision', supplemental: 'Supplemental',
    life: 'Life', iul: 'IUL', term: 'Term',
    fex: 'Final Expense', final_expense: 'Final Expense',
    whole_life: 'Whole Life', mortgage_protection: 'Mortgage Protection',
  };
  return map[String(lob).toLowerCase()] || lob;
}

function call(person) {
  if (!person?.phone) return;
  window.dispatchEvent(new CustomEvent('crm-dial-request', {
    detail: {
      lead: {
        id:    person.client_id || person.id,
        name:  person.name,
        phone: person.phone,
        email: person.email,
      },
    },
  }));
}

export default function AtlasAgentView({ currentUser, showToast = () => {}, onGotoTab = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab]         = React.useState('crosssell'); // crosssell | reviews | nps | tasks

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/atlas/snapshot');
      setData(r);
    } catch (err) {
      showToast(`ATLAS load failed: ${err.message || err}`, 'error');
      setData({ ok: false, summary: { open_opportunities: 0, reviews_due_30d: 0, nps_due_30d: 0, tasks_open: 0 }, crosssell_queue: [], annual_review_window: [], nps_moments: [], tasks: [] });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  React.useEffect(() => { load(); }, [load]);

  // Defensive summary — spread over defaults so a partial API response
  // (or an error object with summary: {}) can't crash toLocaleString().
  const summary = { open_opportunities: 0, reviews_due_30d: 0, nps_due_30d: 0, tasks_open: 0, ...(data?.summary || {}) };

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-950 to-amber-950/30 border border-amber-900/30 rounded-xl p-5 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/80 flex items-center gap-2">
              <Briefcase size={11}/> ATLAS · book-of-business steward
            </div>
            <h1 className="text-xl font-black mt-1.5">Grow what you already won.</h1>
            <p className="text-xs text-slate-400 mt-1">
              {data?.is_demo
                ? 'Demo · synthesized cross-sell + review + NPS queues so the flow is clickable.'
                : 'Spans Health + Life. Cross-sell gaps from client_coverages · review windows from issue_date · NPS milestones at 60d / 1yr / 2yr / 5yr.'}
            </p>
          </div>
          <button onClick={load} disabled={loading} className="text-xs text-amber-200/70 hover:text-amber-200 inline-flex items-center gap-1 disabled:opacity-30">
            <RefreshCcw size={11}/> Refresh
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-px bg-amber-900/20 rounded-lg overflow-hidden">
          <Stat label="Opportunities"  value={summary.open_opportunities.toLocaleString()} sub="cross-sell"/>
          <Stat label="Reviews ≤30d"   value={summary.reviews_due_30d.toLocaleString()}    sub="anniversaries"/>
          <Stat label="NPS moments"    value={summary.nps_due_30d.toLocaleString()}        sub="check-ins"/>
          <Stat label="Open tasks"     value={summary.tasks_open.toLocaleString()}         sub="ATLAS queue"/>
        </div>

        {/* Sub-tabs */}
        <div className="mt-4 inline-flex border border-amber-900/30 rounded-full overflow-hidden">
          {[
            { key: 'crosssell', label: 'Cross-sell',  count: data?.crosssell_queue?.length || 0 },
            { key: 'reviews',   label: 'Reviews',     count: data?.annual_review_window?.length || 0 },
            { key: 'nps',       label: 'NPS',         count: data?.nps_moments?.length || 0 },
            { key: 'tasks',     label: 'Tasks',       count: data?.tasks?.length || 0 },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 inline-flex items-center gap-1.5 ${tab === t.key ? 'bg-amber-500 text-black' : 'text-amber-200/70 hover:text-amber-200'}`}
            >
              {t.label}
              <span className={`text-[10px] px-1.5 rounded-full ${tab === t.key ? 'bg-black/20' : 'bg-amber-900/40'}`}>{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        {loading && !data ? (
          <div className="text-center py-12 text-slate-500"><Loader2 size={20} className="animate-spin inline mr-2"/>Loading book intelligence…</div>
        ) : tab === 'crosssell' ? (
          <CrosssellQueue rows={data?.crosssell_queue || []} onGotoTab={onGotoTab}/>
        ) : tab === 'reviews' ? (
          <ReviewWindow rows={data?.annual_review_window || []}/>
        ) : tab === 'nps' ? (
          <NpsMoments rows={data?.nps_moments || []}/>
        ) : (
          <TaskQueue rows={data?.tasks || []} onGotoTab={onGotoTab}/>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-slate-950 px-3 py-2 text-center">
      <div className="text-2xl font-black tabular-nums text-amber-400">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{label}</div>
      {sub && <div className="text-[9px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function CrosssellQueue({ rows, onGotoTab }) {
  if (!rows.length) {
    return (
      <Empty icon={Sparkles} title="No cross-sell gaps surfaced yet" body="Either your book has full coverage on every client (unlikely) or the client_coverages table isn't populated. Run ANCHOR's cross-sell scan to fill it in."/>
    );
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Top opportunities · sorted by score</div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={r.client_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-amber-50/30">
            <div className="text-[11px] font-bold text-slate-400 w-6 tabular-nums">#{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900 text-sm truncate">{r.name}</span>
                {r.state && <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{r.state}</span>}
                <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wide">{lobLabel(r.line_of_business)}</span>
              </div>
              <div className="text-[11px] text-slate-600 truncate">
                <span className="font-mono">{r.phone || '—'}</span>
                <span className="mx-2">·</span>
                {r.carrier || '—'}
                <span className="mx-2">·</span>
                <span className="text-emerald-700 font-semibold">{fmtCents(r.premium_monthly_cents)}</span>
              </div>
              {r.gap_list?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {r.gap_list.slice(0, 5).map(g => (
                    <span key={g} className="text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded uppercase tracking-wide">
                      no {g}
                    </span>
                  ))}
                  {r.gap_list.length > 5 && <span className="text-[9px] text-slate-500">+{r.gap_list.length - 5} more</span>}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-lg font-black text-amber-700 tabular-nums">{r.opportunity_score}</div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">score</div>
            </div>
            <button
              onClick={() => call(r)}
              disabled={!r.phone}
              title={r.phone ? `Call ${r.name}` : 'No phone on file'}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 px-2.5 py-1.5 rounded-md"
            >
              <Phone size={11}/> Call
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[10px] text-slate-500">
        Run ANCHOR's cross-sell scan to refresh coverage gaps · <button onClick={() => onGotoTab('clients')} className="text-amber-700 font-bold hover:underline">Open book</button>
      </div>
    </div>
  );
}

function ReviewWindow({ rows }) {
  if (!rows.length) {
    return <Empty icon={Calendar} title="No reviews due in the next 90 days" body="ATLAS scans every active policy's anniversary and surfaces clients in the 30 / 60 / 90 day window. Check back closer to your book's anniversary peaks."/>;
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Annual review window · next 90 days</div>
      <div className="space-y-1.5">
        {rows.map(r => {
          const urgent = r.days_until <= 14;
          const soon   = !urgent && r.days_until <= 30;
          const band   = urgent ? 'border-rose-300 bg-rose-50/50' : soon ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200';
          const pill   = urgent ? 'bg-rose-600 text-white' : soon ? 'bg-amber-500 text-black' : 'bg-slate-200 text-slate-700';
          return (
            <div key={r.client_id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${band}`}>
              <div className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded ${pill} tabular-nums whitespace-nowrap`}>
                {r.days_until === 0 ? 'TODAY' : `${r.days_until}d`}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900 text-sm truncate">{r.name}</span>
                  <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wide">{lobLabel(r.line_of_business)}</span>
                </div>
                <div className="text-[11px] text-slate-600 truncate">
                  <span className="font-mono">{r.phone || '—'}</span>
                  <span className="mx-2">·</span>
                  {r.carrier || '—'}
                  {r.policy && <><span className="mx-2">·</span>{r.policy}</>}
                </div>
              </div>
              <button
                onClick={() => call(r)}
                disabled={!r.phone}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 px-2.5 py-1.5 rounded-md"
              >
                <Phone size={11}/> Call for review
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NpsMoments({ rows }) {
  if (!rows.length) {
    return <Empty icon={Heart} title="No NPS moments today" body="ATLAS surfaces clients at 60 days, 1 year, 2 years, and 5 years post-issue — the windows where a check-in lifts retention AND generates referrals."/>;
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Happy-call moments · referral generators</div>
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.client_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-pink-200 bg-pink-50/40">
            <Heart size={14} className="text-pink-600 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900 text-sm truncate">{r.name}</span>
                <span className="text-[9px] font-bold text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded uppercase tracking-wide">{r.milestone}</span>
              </div>
              <div className="text-[11px] text-slate-600 truncate">
                <span className="font-mono">{r.phone || '—'}</span>
                <span className="mx-2">·</span>
                {r.carrier || '—'}
                <span className="mx-2">·</span>
                {fmtDays(r.days_since_issue)} since issue
              </div>
            </div>
            <button
              onClick={() => call(r)}
              disabled={!r.phone}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 px-2.5 py-1.5 rounded-md"
            >
              <Phone size={11}/> Check in
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TaskQueue({ rows, onGotoTab }) {
  if (!rows.length) {
    return <Empty icon={Briefcase} title="No open ATLAS tasks" body="ATLAS tasks are created from agent text or AI parsing. Mention an annual review, cross-sell, or NPS check-in and the parser routes it here."/>;
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Open ATLAS tasks</div>
      <div className="space-y-1.5">
        {rows.map(t => (
          <button
            key={t.id}
            onClick={() => onGotoTab('tasks')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-amber-50/30 text-left"
          >
            <Briefcase size={14} className="text-amber-600 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-900 truncate">{t.title}</div>
              <div className="text-[11px] text-slate-500">{t.kind || 'task'} · created {new Date(t.created_at).toLocaleDateString()}</div>
            </div>
            <ChevronRight size={14} className="text-slate-400"/>
          </button>
        ))}
      </div>
    </div>
  );
}

function Empty({ icon: Icon, title, body }) {
  return (
    <div className="text-center py-10 px-4">
      <Icon size={28} className="mx-auto text-slate-300 mb-2"/>
      <div className="font-bold text-slate-700">{title}</div>
      <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">{body}</div>
    </div>
  );
}
